import { BasicInput } from "@/view/components/BasicInput";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/view/components/ui/alert-dialog";
import { Button } from "@/view/components/ui/button";
import { Spinner } from "@/view/components/ui/spinner";
import { FormProvider } from "react-hook-form";
import { useEditCommissionModalController } from "./useEditCommissionModalController";
import { PartnerType } from "@/app/utils/partners.enum";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { percentageMask } from "@/app/utils/comissionPercentage";
import { moneyMask, formatMoneyValue } from "@/app/utils/formatMoneyValue";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/view/components/TabsTable";
import { useState } from "react";
import { useAllPartnerTables } from "@/app/hooks/useAllPartnerTables";
import {
  isMoneyCommissionPartner,
  isSingleCommissionPartner,
  stripCltPrefix,
} from "@/app/utils/partners";

import { PartnerCommissionConfig } from "@/app/services/usersService/userUpdate";

import { useQuery } from "@tanstack/react-query";
import { configService } from "@/app/services/configService";
import { useProductVisibility } from "@/app/hooks/useProductVisibility";

interface EditCommissionModalProps {
  open: boolean;
  onClose: () => void;
  sellerId: string;
  commissions?: Record<PartnerType, PartnerCommissionConfig>;
}

const isFgtsLikePartner = (type: PartnerType) =>
  type === PartnerType.FGTS || type === PartnerType.CLT;

export const EditCommissionModal: React.FC<EditCommissionModalProps> = ({
  open,
  onClose,
  sellerId,
  commissions,
}) => {
  const { form, isSubmitting, handleSubmit, onSubmit } =
    useEditCommissionModalController({ open, onClose, sellerId, commissions });

  const [activeTab, setActiveTab] = useState<string>("");

  const { data: productsConfig } = useQuery({
    queryKey: ["productsConfig"],
    queryFn: configService.getProductsConfig,
  });

  const { isProductVisible } = useProductVisibility();

  const partners = productsConfig?.products
    ? (Object.keys(productsConfig.products) as PartnerType[]).filter(
        (partner) => isProductVisible(partner),
      )
    : [];

  if (!activeTab && partners.length > 0) {
    setActiveTab(partners[0]);
  }

  const { getProductLabel } = useProductsCatalog();

  const { getTablesByPartner, isLoading: isLoadingTables } =
    useAllPartnerTables();

  const createMaxPercentageMask =
    (maxValue: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      percentageMask(e);
      const input = e.currentTarget;
      const numericValue = parseFloat(
        input.value.replace(",", ".").replace("%", ""),
      );
      if (!isNaN(numericValue) && numericValue > maxValue) {
        input.value = `${maxValue.toFixed(2).replace(".", ",")}%`;
      }
    };

  const renderCommissionInput = (partner: PartnerType) => {
    const partnerConfig = productsConfig?.products[partner];
    const partnerTables = getTablesByPartner(partner);
    const isCurrency = isMoneyCommissionPartner(partner);
    const isRangeCurrency = isCurrency || partner === PartnerType.PAN;
    const isFgtsLike = isFgtsLikePartner(partner);
    const isSimplePercentPartner = isSingleCommissionPartner(partner);

    if (isSimplePercentPartner) {
      const configItems = partnerConfig?.items || [];
      const maxItem = configItems.find((item: any) => item.label === "max");
      const tableId = partnerConfig?._id ?? partnerTables[0]?._id;

      return (
        <div className="flex max-h-[400px] flex-col gap-5 overflow-y-auto py-5">
          <div className="rounded-xl border bg-card p-4">
            <h3 className="text-lg font-semibold">
              {stripCltPrefix(getProductLabel(partner))}
            </h3>
            {maxItem && (
              <p className="text-xs text-black04">Máximo {maxItem.value}%</p>
            )}
            <div className="mt-3">
              <p className="mb-1 text-xs font-bold">Comissão</p>
              <BasicInput
                name={`${partner}_${tableId}_percents`}
                className="max-w-[180px]"
                style={{ backgroundColor: "#F7F7F7" }}
                variant="percentage"
                onInput={percentageMask}
              />
            </div>
          </div>
        </div>
      );
    }

    if (partner === PartnerType.CLT) {
      const hasFaixas = partnerTables.some(
        (table: any) => table.valuesAndCommissions?.length > 0,
      );

      if (hasFaixas) {
        return (
          <div className="flex max-h-[400px] flex-col gap-5 overflow-y-auto py-5">
            {partnerTables.flatMap((table: any) =>
              (table.valuesAndCommissions || []).map(
                (vc: any, vcIndex: number) => {
                  const max = vc.max ?? table.maxPercents ?? 0;

                  return (
                    <div
                      key={`${table._id}-${vcIndex}`}
                      className="rounded-xl border bg-card p-4"
                    >
                      <h3 className="text-lg font-semibold">
                        A partir de {vc.value}{" "}
                        {vc.value === 1 ? "parcela" : "parcelas"}
                      </h3>
                      <p className="text-xs text-black04">
                        Tabela {table.name}
                      </p>
                      {!!max && (
                        <p className="text-xs text-black04">Máximo {max}%</p>
                      )}
                      <div className="mt-3">
                        <p className="mb-1 text-xs font-bold">Comissão</p>
                        <BasicInput
                          name={`${PartnerType.CLT}_${table._id}_${vcIndex}`}
                          className="max-w-[180px]"
                          style={{ backgroundColor: "#F7F7F7" }}
                          variant="percentage"
                          onInput={createMaxPercentageMask(max)}
                        />
                      </div>
                    </div>
                  );
                },
              ),
            )}
          </div>
        );
      }
    }

    if (partnerTables?.length > 0) {
      return (
        <div className="flex max-h-[400px] flex-col gap-5 overflow-y-auto py-5">
          {partnerTables.map((table: any) => {
            if (
              table.valuesAndCommissions &&
              table.valuesAndCommissions.length > 0 &&
              !isFgtsLike
            ) {
              return table.valuesAndCommissions.map(
                (vc: any, vcIndex: number) => (
                  <div
                    key={`${table._id}-${vcIndex}`}
                    className="rounded-xl border bg-card p-4"
                  >
                    <h3 className="text-lg font-semibold">
                      {isRangeCurrency
                        ? `A partir de ${formatMoneyValue(vc.value)}`
                        : `A partir de ${vc.value} parcelas`}
                    </h3>
                    <p className="text-xs text-black04">
                      Máximo{" "}
                      {isRangeCurrency
                        ? formatMoneyValue(vc.max || 0)
                        : `${vc.max || table.maxPercents || 0}%`}
                    </p>
                    <div className="mt-3">
                      <p className="mb-1 text-xs font-bold">Comissão</p>
                      <BasicInput
                        name={`${partner}_${table._id}_${vcIndex}`}
                        className="max-w-[180px]"
                        style={{ backgroundColor: "#F7F7F7" }}
                        variant={isCurrency ? "default" : "percentage"}
                        onInput={isCurrency ? moneyMask : percentageMask}
                      />
                    </div>
                  </div>
                ),
              );
            }

            return (
              <div key={table._id} className="rounded-xl border bg-card p-4">
                <h3 className="text-lg font-semibold">
                  {partner === PartnerType.CLT
                    ? `${table.name} parcelas`
                    : table.name}
                </h3>
                {table?.maxPercents && (
                  <p className="text-xs text-black04">
                    Máximo {table.maxPercents || 0}%
                  </p>
                )}
                <div className="mt-3">
                  <p className="mb-1 text-xs font-bold">Comissão</p>
                  <BasicInput
                    name={`${partner}_${table._id}_supervisor`}
                    className="max-w-[180px]"
                    style={{ backgroundColor: "#F7F7F7" }}
                    variant="percentage"
                    onInput={createMaxPercentageMask(table.maxPercents || 0)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    const configItems = partnerConfig?.items || [];

    if (configItems.length === 0) {
      return (
        <div className="flex max-h-[400px] flex-col gap-5 overflow-y-auto py-5">
          <div className="rounded-xl border bg-card p-4">
            <h3 className="mb-3 text-lg font-semibold">
              {partnerConfig?.name}
            </h3>
            <p className="mb-3 text-xs text-black04">
              Nenhuma tabela configurada
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex max-h-[400px] flex-col gap-5 overflow-y-auto py-5">
        {configItems.map((item: any, index: number) => (
          <div key={index} className="rounded-xl border bg-card p-4">
            <h3 className="text-lg font-semibold">
              {isCurrency
                ? `A partir de ${formatMoneyValue(item.label)}`
                : `A partir de ${item.label} parcelas`}
            </h3>
            {item.max !== undefined && (
              <p className="text-xs text-black04">
                Máximo{" "}
                {isCurrency ? formatMoneyValue(item.max) : `${item.max}%`}
              </p>
            )}
            <div className="mt-3">
              <p className="mb-1 text-xs font-bold">Comissão</p>
              <BasicInput
                name={`${partner}_${partnerConfig?._id}_${index}`}
                className="max-w-[180px]"
                style={{ backgroundColor: "#F7F7F7" }}
                variant={isCurrency ? "default" : "percentage"}
                onInput={isCurrency ? moneyMask : percentageMask}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-h-[90vh] w-[720px] max-w-[95vw] overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-full max-h-[90vh] min-w-0 flex-col"
        >
          <FormProvider {...form}>
            <Tabs
              defaultValue={PartnerType.FGTS}
              value={activeTab}
              onValueChange={setActiveTab}
            >
              <div className="flex items-center justify-between px-4 pt-6">
                <TabsList className="flex items-center gap-8 overflow-x-auto overflow-y-hidden">
                  {partners.map((partner) => (
                    <TabsTrigger
                      key={partner}
                      className="whitespace-nowrap pb-2 text-xl data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold"
                      value={partner}
                    >
                      {getProductLabel(partner)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              <div className="flex-1 overflow-y-auto px-4">
                {partners.map((partner) => (
                  <TabsContent key={partner} value={partner}>
                    {isLoadingTables ? (
                      <div className="flex justify-center py-10">
                        <Spinner />
                      </div>
                    ) : (
                      renderCommissionInput(partner)
                    )}
                  </TabsContent>
                ))}
              </div>
            </Tabs>
          </FormProvider>

          <AlertDialogFooter className="flex-shrink-0 space-x-2 border-t-[1px] border-white02 p-5">
            <AlertDialogCancel
              className="rounded-full bg-white02"
              type="button"
              onClick={onClose}
            >
              Cancelar
            </AlertDialogCancel>
            <Button
              disabled={isSubmitting || isLoadingTables}
              type="submit"
              className="rounded-full bg-primary font-semibold text-white"
            >
              {isSubmitting ? <Spinner size={"small"} /> : "Salvar alterações"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
